import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Flashbar,
  Header,
  Link,
  Pagination,
  Popover,
  SpaceBetween,
  Table,
} from "@amzn/awsui-components-react";
import Multiselect from "@amzn/awsui-components-core/multiselect";
import Select from "@amzn/awsui-components-core/select";
import Input from "@amzn/awsui-components-core/input";
import Checkbox from "@amzn/awsui-components-core/checkbox";
import RadioButton from "@amzn/awsui-components-core/radio-button";
import { OptionDefinition } from "@amzn/awsui-components-core/internal/components/option/interfaces";
import * as tokens from "@amzn/awsui-design-tokens-core";
import { useRecentTransactions } from "../hooks/useRecentTransactions";
import {
  applyPaymentProvider,
  applyPaymentType,
  COLUMN_DEFINITIONS,
  DEFAULT_OPTION,
  getFeatureGroup,
  getModifiedOptions,
  getPaymentTypeOptions,
  filterGlOptions,
  getGlOption,
  CUSTOM_TIME_RANGE_VALUE,
  GROUPED_FEATURES,
  GROUPED_MARKETPLACES,
  GROUPED_USECASES,
  PAYMENT_METHOD_CLASS_OPTIONS,
  PAYMENT_METHOD_OPTIONS,
  RECENT_TRXN_EMPTY_STATE,
  STAGES,
  TABLE_PREFERENCES,
  TIME_RANGES,
} from "../config/recentTransactionTableConfig";
import ViewLogs from "./common/ViewLogs";
import { publisherLogGroups } from "../helpers/logUtils";
import { EmptyState } from "./common/EmptyState";
import { Transaction, Stage } from "../lusca-search-api/generated-src";
import { SearchFilters } from "../types/common";
import { useFolderUI } from "../hooks/useFolderUI";
import useCurrentUser from "../hooks/useCurrentUser";
import useFolders from "../hooks/useFolders";
import { FolderModal } from "./folders/FolderModal";
import { SavedSearchesManager } from "./SavedSearchesManager";
import { SavedSearch } from "../hooks/useSavedSearches";
import { FeaturesAsinFooter } from "./FeaturesAsinFooter";
import {
  CUSTOM_DATE_OPTION_ATTRIBUTE,
  TimeRangeCustomDateFooter,
  TimeRangeCustomDateOption,
} from "./TimeRangeCustomDateFooter";
import {
  parseFiltersFromUrl,
  serializeFilterState,
} from "../helpers/recentTransactionsUtils";

// Selected-token stand-in for the ASIN, which lives in the Features dropdown.
const ASIN_TOKEN_VALUE = "__ASIN__";
const RESET_OPTION_VALUE = "__RESET__";

const TIME_RANGE_PRESETS = TIME_RANGES.filter(
  (range) => range.value !== CUSTOM_TIME_RANGE_VALUE
);
const CUSTOM_TIME_RANGE = TIME_RANGES.find(
  (range) => range.value === CUSTOM_TIME_RANGE_VALUE
) ?? { value: CUSTOM_TIME_RANGE_VALUE, label: "Custom date" };
// "Custom date" goes last, under the presets.
const TIME_RANGE_OPTIONS = [...TIME_RANGE_PRESETS, CUSTOM_TIME_RANGE];

// True when the element is the "Custom date" option or inside it.
const isCustomDateOption = (element: Element | null) =>
  !!element
    ?.closest('[role="option"]')
    ?.querySelector(`[${CUSTOM_DATE_OPTION_ATTRIBUTE}]`);

interface RecentTransactionsProps {
  isRaven?: boolean;
  selectedOrders?: Transaction[];
  setSelectedOrders?: (orders: Transaction[]) => void;
}

export default function RecentTransactions(props: RecentTransactionsProps) {
  const [selectedTransactions, setSelectedTransactions] = useState<
    Transaction[]
  >([]);
  const { folders, addTransactions, saveFolder } = useFolders();
  const {
    isModalVisible,
    isFolderModalVisible,
    selectedFolderId,
    folderName,
    error,
    setIsModalVisible,
    setSelectedFolderId,
    setFolderName,
    setNotification,
    handleModalDismiss,
    handleCreateFolder,
    handleFolderModalDismiss,
    handleSaveFolder,
    getFlashbarItems,
    flashbarStyles,
  } = useFolderUI();

  const {
    items,
    collectionProps,
    filterProps,
    paginationProps,
    handleSearch,
    isLoading,
    selectedFilters,
    setSelectedFilters,
    clearFilter,
    executedQuery,
    logGroups,
    error: searchError,
    retrySearch,
  } = useRecentTransactions();
  // Logs cover the searched filters, so they follow the Stage and Dev user
  // filters rather than any one result.
  const logsUser =
    selectedFilters.stage.value === Stage.Dev
      ? selectedFilters.devUser || undefined
      : undefined;

  // Without a user the backend searches the caller's own Dev stack, so show them.
  const currentUser = useCurrentUser();
  useEffect(() => {
    if (currentUser && !selectedFilters.devUser) {
      setSelectedFilters((filters) =>
        filters.devUser ? filters : { ...filters, devUser: currentUser }
      );
    }
  }, [currentUser, selectedFilters.devUser, setSelectedFilters]);

  const searchErrorStatus =
    searchError && axios.isAxiosError(searchError)
      ? searchError.response?.status
      : undefined;
  const canRetrySearch = searchErrorStatus === 503 || searchErrorStatus === 504;
  const searchErrorState = useMemo(() => {
    if (searchErrorStatus === 504) {
      return {
        heading: "Search did not complete",
        message:
          "The search exceeded its time limit. Narrow the filters or retry the search.",
      };
    }
    if (searchErrorStatus === 503) {
      return {
        heading: "Search failed",
        message: "The transaction search could not finish. Please retry.",
      };
    }
    if (searchErrorStatus === 400) {
      return {
        heading: "Invalid search filters",
        message: "Review the selected filters and search again.",
      };
    }
    return RECENT_TRXN_EMPTY_STATE.error;
  }, [searchErrorStatus]);

  const modifiedMarketplaces = useMemo(
    () =>
      getModifiedOptions(GROUPED_MARKETPLACES, selectedFilters.marketplaces),
    [selectedFilters.marketplaces]
  );

  const modifiedUsecases = useMemo(
    () => getModifiedOptions(GROUPED_USECASES, selectedFilters.usecases),
    [selectedFilters.usecases]
  );

  const [glSearchText, setGlSearchText] = useState("");
  const [customDateOpen, setCustomDateOpen] = useState(false);
  const isCustomDate =
    selectedFilters.timeRange.value === CUSTOM_TIME_RANGE_VALUE;
  // A chosen custom date shows in the closed Time Range field itself.
  const timeRangeSelection: OptionDefinition = isCustomDate
    ? {
        value: CUSTOM_TIME_RANGE_VALUE,
        label: selectedFilters.date
          ? selectedFilters.date.split("-").reverse().join("/")
          : "Custom date",
      }
    : selectedFilters.timeRange;

  const paymentTypeOptions = useMemo(
    () => getPaymentTypeOptions(selectedFilters.paymentMethod),
    [selectedFilters.paymentMethod]
  );

  const modifiedFeatures = useMemo(() => {
    const features = GROUPED_FEATURES;

    // Add reset button as first item if there are selections
    if (selectedFilters.features.length > 0 || selectedFilters.asin) {
      return [
        { label: RESET_OPTION_VALUE, value: RESET_OPTION_VALUE },
        ...features,
      ];
    }

    return features;
  }, [selectedFilters.features, selectedFilters.asin]);

  // The applied ASIN shows as the first token in the Features trigger.
  const featuresSelectedOptions = useMemo(
    () =>
      selectedFilters.asin
        ? [
            {
              label: `ASIN: ${selectedFilters.asin}`,
              value: ASIN_TOKEN_VALUE,
            },
            ...selectedFilters.features,
          ]
        : selectedFilters.features,
    [selectedFilters.features, selectedFilters.asin]
  );

  // Custom renderer for Features multiselect
  // Show unselectable group headers and then radio buttons or checkboxes
  const renderFeatureOption = ({
    item,
  }: {
    item: {
      type: string;
      option?: { value?: string; label?: string };
      parent?: unknown;
      disabled?: boolean;
    };
  }) => {
    // Handle reset button
    if (item.option?.value === RESET_OPTION_VALUE) {
      return (
        <div
          style={{
            padding: tokens.spaceScaledS,
            borderBottom: `1px solid ${tokens.colorBorderDividerDefault}`,
            textAlign: "center",
            backgroundColor: tokens.colorBackgroundContainerContent,
          }}
        >
          <Button
            variant="inline-link"
            iconName="close"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedFilters({
                ...selectedFilters,
                features: [],
                asin: "",
              });
            }}
          >
            Reset all selections
          </Button>
        </div>
      );
    }

    // Display group name - clicking clears the group
    if (item.type === "group") {
      return (
        <div
          style={{
            padding: `${tokens.spaceScaledXxs} ${tokens.spaceScaledS}`,
            fontWeight: "bold",
            cursor: "pointer",
          }}
          onClick={(e) => {
            e.stopPropagation();
            const groupLabel = item.option?.label;
            if (groupLabel) {
              const filtered = selectedFilters.features.filter(
                (opt) => getFeatureGroup(opt.value!)?.label !== groupLabel
              );
              setSelectedFilters({ ...selectedFilters, features: filtered });
            }
          }}
        >
          {item.option?.label}
        </div>
      );
    }

    if (item.type !== "item" || !item.option) return null;

    const group = getFeatureGroup(item.option.value || "");
    const isSelected = selectedFilters.features.some(
      (f) => f.value === item.option?.value
    );

    return (
      <div
        style={{
          padding: `${tokens.spaceScaledXxs} ${tokens.spaceScaledS}`,
          paddingInlineStart: item.parent
            ? tokens.spaceScaledXxl
            : tokens.spaceScaledS,
        }}
      >
        {group?.multiSelect ? (
          <Checkbox checked={isSelected}>{item.option.label}</Checkbox>
        ) : (
          <RadioButton
            checked={isSelected}
            value=""
            name={group?.label || "feature"}
          >
            {item.option.label}
          </RadioButton>
        )}
      </div>
    );
  };

  const handleMultiselectChange =
    <K extends keyof SearchFilters>(filter: K) =>
    ({
      detail,
    }: {
      detail: { selectedOptions: readonly OptionDefinition[] };
    }) => {
      let newSelection = [...detail.selectedOptions] as OptionDefinition[];
      const currentSelection = selectedFilters[filter] as OptionDefinition[];
      const hadAll = currentSelection.some(
        (option) => option.value === DEFAULT_OPTION.value
      );
      const hasAll = newSelection.some(
        (option) => option.value === DEFAULT_OPTION.value
      );

      if (hasAll && !hadAll) {
        newSelection = [DEFAULT_OPTION];
      } else if (hadAll) {
        newSelection = [];
      }
      setSelectedFilters({
        ...selectedFilters,
        [filter]: newSelection,
      });
    };

  const handleFeaturesChange = ({
    detail,
  }: {
    detail: { selectedOptions: readonly OptionDefinition[] };
  }) => {
    const newSelection = (
      [...detail.selectedOptions] as OptionDefinition[]
    ).filter(
      (opt) =>
        opt.value &&
        opt.value !== RESET_OPTION_VALUE &&
        opt.value !== ASIN_TOKEN_VALUE
    );
    const prevSelection = selectedFilters.features;

    const addedItems = newSelection.filter(
      (opt) => !prevSelection.some((p) => p.value === opt.value)
    );

    // No new items selected, so user deselected items from the group
    if (addedItems.length === 0) {
      setSelectedFilters({ ...selectedFilters, features: newSelection });
      return;
    }

    const group = getFeatureGroup(addedItems[0].value!);

    // Radio buttons - only keep the latest selection
    if (group && !group.multiSelect) {
      const filtered = newSelection.filter(
        (opt) =>
          getFeatureGroup(opt.value!)?.label !== group.label ||
          opt.value === addedItems[0].value
      );
      setSelectedFilters({ ...selectedFilters, features: filtered });
      return;
    }

    setSelectedFilters({ ...selectedFilters, features: newSelection });
  };

  const handleAddToFolder = () => {
    if (selectedTransactions.length === 0) {
      return;
    }
    setIsModalVisible(true);
  };

  const handleFolderSelect = () => {
    if (selectedFolderId && selectedTransactions.length > 0) {
      const result = addTransactions(
        selectedFolderId,
        selectedTransactions.map((transaction) => ({
          date: new Date().toISOString(),
          id: transaction.id || "",
          // The stage the transaction was found in, not the Stage filter,
          // which may have changed since the search.
          stage: transaction.stage,
          user: transaction.user,
          status: "unknown",
          country: transaction.country,
          // The code; the folder table shows its name.
          gl: transaction.gl || "",
          usecase: transaction.usecase || "",
          paymentMethod: transaction.paymentMethod || "",
          labels: [],
          consumptionTypes: [],
        }))
      );

      setNotification({
        type: result.success ? "success" : "error",
        message: result.message,
      });

      if (result.success) {
        setSelectedTransactions([]);
        handleModalDismiss();
      }
    }
  };

  const tableFooter = useMemo(() => {
    if (isLoading || items.length >= 15 || selectedFilters.deep) {
      return null;
    }

    return (
      <Box textAlign="center">
        {items.length === 0
          ? "No results found. "
          : "Limited results found in default search window. "}
        <Link
          onFollow={() => {
            setSelectedFilters((f) => ({ ...f, deep: true }));
            handleSearch({ deep: true });
          }}
        >
          Search deeper?
        </Link>{" "}
      </Box>
    );
  }, [
    items.length,
    selectedFilters.deep,
    isLoading,
    handleSearch,
    setSelectedFilters,
  ]);

  const loadingText = useMemo(() => {
    if (!isLoading) return "";
    return selectedFilters.deep
      ? "Performing deep search (this may take longer)..."
      : "Searching for transactions";
  }, [isLoading, selectedFilters.deep]);

  const getCurrentFilters = () =>
    serializeFilterState(selectedFilters).toString();

  const handleLoadSearch = (search: SavedSearch) => {
    const filters = parseFiltersFromUrl(new URLSearchParams(search.filters));
    setSelectedFilters(filters);
    handleSearch(filters);
  };

  let emptyStateAction: { text: string; onClick: () => void } | undefined;
  if (searchError && canRetrySearch && !isLoading) {
    emptyStateAction = {
      text: "Retry search",
      onClick: retrySearch ?? (() => handleSearch()),
    };
  } else if (
    !searchError &&
    !selectedFilters.deep &&
    !filterProps.filteringText
  ) {
    emptyStateAction = {
      text: "Search deeper?",
      onClick: () => handleSearch({ deep: true }),
    };
  }

  return (
    <>
      <div style={flashbarStyles}>
        <Flashbar items={getFlashbarItems()} />
        {searchError && canRetrySearch && items.length > 0 && (
          <Flashbar
            items={[
              {
                type: "error",
                header: searchErrorState.heading,
                content: searchErrorState.message,
                action: (
                  <Button
                    loading={isLoading}
                    disabled={isLoading}
                    onClick={retrySearch ?? (() => handleSearch())}
                  >
                    Retry search
                  </Button>
                ),
              },
            ]}
          />
        )}
      </div>
      <Table
        {...collectionProps}
        {...TABLE_PREFERENCES}
        resizableColumns
        columnDefinitions={COLUMN_DEFINITIONS}
        items={items}
        loading={isLoading}
        loadingText={loadingText}
        selectionType="multi"
        onSelectionChange={({ detail }) => {
          if (props.isRaven && props.setSelectedOrders) {
            props.setSelectedOrders(detail.selectedItems);
          } else if (!props.isRaven) {
            setSelectedTransactions(detail.selectedItems);
          }
        }}
        selectedItems={
          props.isRaven ? props.selectedOrders : selectedTransactions
        }
        header={
          <Header
            variant="awsui-h1-sticky"
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <ViewLogs
                  stage={selectedFilters.stage.value as Stage}
                  user={logsUser}
                  logGroups={
                    logGroups && logGroups.length > 0
                      ? logGroups
                      : publisherLogGroups(
                          selectedFilters.stage.value as Stage,
                          logsUser
                        )
                  }
                  logQuery={executedQuery!}
                  minimal
                />
                {!props.isRaven && (
                  <Button
                    onClick={handleAddToFolder}
                    disabled={selectedTransactions.length === 0}
                  >
                    Add to Folder
                  </Button>
                )}
                {!props.isRaven && (
                  <SavedSearchesManager
                    getFilters={getCurrentFilters}
                    stage={selectedFilters.stage.value as Stage}
                    onLoadSearch={handleLoadSearch}
                  />
                )}
              </SpaceBetween>
            }
          >
            Find Transactions{" "}
            <Popover
              dismissButton={false}
              fixedWidth={false}
              position="bottom"
              triggerType="custom"
              content="Searches look at the last 2 weeks of logs from your specified start time. To choose a specific start time range, or add custom filters, open in CloudWatch"
            >
              <Button iconName="status-info" variant="inline-icon" />
            </Popover>
          </Header>
        }
        filter={
          <div className="input-container">
            <div className="select-filter stage-select-filter">
              <Select
                data-testid="stage-filter"
                inlineLabelText="Stage"
                options={STAGES}
                selectedAriaLabel="Selected"
                selectedOption={selectedFilters.stage}
                onChange={({ detail }) =>
                  setSelectedFilters({
                    ...selectedFilters,
                    stage: detail.selectedOption,
                  })
                }
                expandToViewport
              />
            </div>
            {selectedFilters.stage.value === Stage.Dev && (
              <div className="select-filter dev-user-filter">
                <Input
                  data-testid="dev-user-filter"
                  inlineLabelText="User"
                  value={selectedFilters.devUser}
                  onChange={({ detail }) =>
                    setSelectedFilters({
                      ...selectedFilters,
                      devUser: detail.value.trim(),
                    })
                  }
                />
              </div>
            )}
            <div className="select-filter gl-select-filter">
              <Select
                inlineLabelText="GL"
                data-testid="gl-filter"
                options={filterGlOptions(glSearchText)}
                selectedAriaLabel="Selected"
                selectedOption={getGlOption(selectedFilters.gl.value)}
                onChange={({ detail }) =>
                  setSelectedFilters({
                    ...selectedFilters,
                    gl: getGlOption(detail.selectedOption.value),
                  })
                }
                expandToViewport
                // Searched by filterGlOptions so results look like the list.
                filteringType="manual"
                onLoadItems={({ detail }) =>
                  setGlSearchText(detail.filteringText)
                }
                filteringPlaceholder="Find GL"
                empty="No matching GLs"
              />
            </div>
            <div className="select-filter payment-provider-select-filter">
              <Select
                inlineLabelText="Payment Provider"
                data-testid="payment-method-filter"
                options={PAYMENT_METHOD_OPTIONS}
                selectedAriaLabel="Selected"
                selectedOption={selectedFilters.paymentMethod}
                onChange={({ detail }) =>
                  setSelectedFilters({
                    ...selectedFilters,
                    ...applyPaymentProvider(
                      selectedFilters,
                      detail.selectedOption
                    ),
                  })
                }
                expandToViewport
              />
            </div>
            <div className="select-filter payment-type-select-filter">
              <Select
                inlineLabelText="Payment Type"
                data-testid="payment-type-filter"
                options={paymentTypeOptions}
                selectedAriaLabel="Selected"
                selectedOption={selectedFilters.paymentType}
                onChange={({ detail }) =>
                  setSelectedFilters({
                    ...selectedFilters,
                    ...applyPaymentType(selectedFilters, detail.selectedOption),
                  })
                }
                expandToViewport
              />
            </div>
            <div className="select-filter payment-class-select-filter">
              <Select
                inlineLabelText="Payment Method Class"
                data-testid="payment-method-class-filter"
                options={PAYMENT_METHOD_CLASS_OPTIONS}
                selectedAriaLabel="Selected"
                selectedOption={selectedFilters.paymentMethodClass}
                onChange={({ detail }) =>
                  setSelectedFilters({
                    ...selectedFilters,
                    paymentMethodClass: detail.selectedOption,
                  })
                }
                expandToViewport
              />
            </div>
            <div className="select-filter multiselect-filter">
              <Multiselect
                placeholder="Marketplaces"
                inlineTokens
                data-testid="marketplace-filter"
                options={modifiedMarketplaces}
                selectedOptions={selectedFilters.marketplaces}
                onChange={handleMultiselectChange("marketplaces")}
                expandToViewport
                filteringType="auto"
                filteringPlaceholder="Find marketplaces"
                empty="No matching marketplaces"
              />
            </div>
            <div className="select-filter multiselect-filter">
              <Multiselect
                placeholder="Usecases"
                inlineTokens
                data-testid="usecase-filter"
                options={modifiedUsecases}
                selectedOptions={selectedFilters.usecases}
                onChange={handleMultiselectChange("usecases")}
                filteringType="auto"
                expandToViewport
                filteringPlaceholder="Find usecases"
                empty="No matching usecases"
              />
            </div>
            <div className="select-filter multiselect-filter features-multiselect">
              <Multiselect
                placeholder="Features & ASIN"
                data-testid="features-filter"
                inlineTokens
                options={modifiedFeatures}
                selectedOptions={featuresSelectedOptions}
                onChange={handleFeaturesChange}
                renderOption={renderFeatureOption}
                filteringType="auto"
                expandToViewport
                filteringPlaceholder="Find features"
                empty="No matching features"
                renderDropdownFooter={({ filterText }) => (
                  <FeaturesAsinFooter
                    appliedAsin={selectedFilters.asin}
                    filterText={filterText}
                    onApply={(asin) =>
                      setSelectedFilters((filters) => ({ ...filters, asin }))
                    }
                    onRemove={() =>
                      setSelectedFilters((filters) => ({
                        ...filters,
                        asin: "",
                      }))
                    }
                  />
                )}
              />
            </div>
            <div
              className="recent-transactions-search-actions"
              data-testid="search-actions"
            >
              {/* "Custom date" is a real option so it highlights like the
                  presets, but picking it swaps the presets for a calendar
                  instead of closing the dropdown. The dropdown renders in a
                  portal yet is still a React child of this div, so these
                  capture handlers run before the Select's own. */}
              <div
                className="select-filter time-range-select-filter"
                onMouseUpCapture={(event) => {
                  if (isCustomDateOption(event.target as Element)) {
                    event.stopPropagation();
                    setCustomDateOpen(true);
                  }
                }}
                onKeyDownCapture={(event) => {
                  if (event.key !== "Enter" && event.key !== " ") return;
                  const activeId = (event.target as Element).getAttribute(
                    "aria-activedescendant"
                  );
                  if (
                    activeId &&
                    isCustomDateOption(document.getElementById(activeId))
                  ) {
                    event.preventDefault();
                    event.stopPropagation();
                    setCustomDateOpen(true);
                  }
                }}
              >
                <Select
                  inlineLabelText="Time Range"
                  data-testid="time-range-filter"
                  // While the calendar is open it replaces the options.
                  options={customDateOpen ? [] : TIME_RANGE_OPTIONS}
                  selectedAriaLabel="Selected"
                  selectedOption={timeRangeSelection}
                  renderOption={({ item }) =>
                    item.type === "item" &&
                    item.option.value === CUSTOM_TIME_RANGE_VALUE ? (
                      <TimeRangeCustomDateOption
                        value={isCustomDate ? selectedFilters.date : ""}
                      />
                    ) : null
                  }
                  onChange={({ detail }) => {
                    // The capture handlers above open the calendar instead.
                    if (
                      detail.selectedOption.value === CUSTOM_TIME_RANGE_VALUE
                    ) {
                      return;
                    }
                    setSelectedFilters({
                      ...selectedFilters,
                      timeRange: detail.selectedOption,
                      date: "",
                    });
                  }}
                  dropdownRole="dialog"
                  renderDropdownFooter={({ closeDropdown }) =>
                    customDateOpen ? (
                      <TimeRangeCustomDateFooter
                        value={isCustomDate ? selectedFilters.date : ""}
                        onBack={() => setCustomDateOpen(false)}
                        onChange={(date) => {
                          setSelectedFilters((filters) => ({
                            ...filters,
                            timeRange: CUSTOM_TIME_RANGE,
                            date,
                          }));
                          closeDropdown();
                        }}
                      />
                    ) : null
                  }
                  expandToViewport
                />
              </div>
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="primary" onClick={() => handleSearch()}>
                  Search
                </Button>
                <Button onClick={clearFilter}>Clear filters</Button>
              </SpaceBetween>
            </div>
          </div>
        }
        pagination={<Pagination {...paginationProps} />}
        empty={
          <EmptyState
            error={searchError}
            filteringText={filterProps.filteringText}
            config={{
              ...RECENT_TRXN_EMPTY_STATE,
              error: searchErrorState,
            }}
            action={emptyStateAction}
          />
        }
        footer={
          items.length > 0 && !isLoading && !searchError ? tableFooter : null
        }
      />

      {!props.isRaven && (
        <FolderModal
          isModalVisible={isModalVisible}
          isFolderModalVisible={isFolderModalVisible}
          isShareModalVisible={false}
          selectedFolderId={selectedFolderId}
          folderName={folderName}
          error={error}
          folders={folders}
          sharingFolder={null}
          onModalDismiss={handleModalDismiss}
          onCreateFolder={handleCreateFolder}
          onFolderSelect={handleFolderSelect}
          onFolderModalDismiss={handleFolderModalDismiss}
          onShareModalDismiss={() => {}}
          onSaveFolder={() => handleSaveFolder(saveFolder)}
          setSelectedFolderId={setSelectedFolderId}
          setFolderName={setFolderName}
        />
      )}
    </>
  );
}
